interface AnalyticsPage {
    url: string;
    props: {
        [key: string]: unknown;
        auth?: { user?: unknown };
        analytics?: { distinctId: string | null };
    };
}

interface AnalyticsPayload {
    [key: string]: unknown;
}

interface Umami {
    track: (payload: (defaults: AnalyticsPayload) => AnalyticsPayload) => void | Promise<unknown>;
    identify: (id: string) => void | Promise<unknown>;
}

interface AnalyticsWindow extends Window {
    umami?: Umami;
    poddtoppenAccountBeforeSend?: (
        type: string,
        payload: AnalyticsPayload,
    ) => AnalyticsPayload | null;
}

type AccountEvent =
    | 'account-play-click'
    | 'account-podcast-unfollow'
    | 'account-episode-unsave'
    | 'account-episode-mark-played'
    | 'account-episode-mark-unplayed';

export type BoostEvent =
    | 'boost-step-view'
    | 'boost-google-start'
    | 'boost-account-created'
    | 'boost-signed-in'
    | 'boost-email-verified'
    | 'boost-purchase'
    | 'boost-purchase-failed'
    | 'boost-order-action';

type EventData = Record<string, string | number | boolean>;

interface Area {
    name: 'account' | 'boost';
    path: string;
    title: string;
    distinctId: string | null;
}

let accountPath: string | null = null;
let accountDistinctId: string | null = null;
let boostPath: string | null = null;
let boostDistinctId: string | null = null;
let identifiedId: string | null = null;
let lastTrackedPath: string | null = null;

/** Where the visitor is, or null when nothing on this page may be tracked. */
function currentArea(): Area | null {
    if (accountPath && accountDistinctId) {
        return {
            name: 'account',
            path: accountPath,
            title: 'Mina sidor',
            distinctId: accountDistinctId,
        };
    }
    if (boostPath) {
        return {
            name: 'boost',
            path: boostPath,
            title: 'Podcast Boost',
            distinctId: boostDistinctId,
        };
    }
    return null;
}

function synchronizeIdentity(): void {
    if (typeof window === 'undefined') return;
    const analyticsWindow = window as AnalyticsWindow;
    analyticsWindow.poddtoppenAccountBeforeSend = (type, payload) => {
        const area = currentArea();
        if (!area) return null;
        if (type !== 'identify') return payload;
        return payload.id ? { ...payload, url: area.path, referrer: '', title: area.title } : null;
    };

    const umami = analyticsWindow.umami;
    const distinctId = currentArea()?.distinctId ?? null;
    if (!umami || identifiedId === distinctId) return;

    try {
        void Promise.resolve(umami.identify(distinctId ?? '')).catch(() => undefined);
        identifiedId = distinctId;
    } catch {
        // Tracking must not interrupt navigation.
    }
}

function send(areaName: Area['name'], name?: string, data: EventData = {}): boolean {
    if (typeof window === 'undefined') return false;
    const area = currentArea();
    if (!area || area.name !== areaName) return false;
    synchronizeIdentity();
    const umami = (window as AnalyticsWindow).umami;
    if (!umami) return false;

    const { path, title, distinctId } = area;
    try {
        void Promise.resolve(
            umami.track((defaults) => ({
                ...defaults,
                id: distinctId ?? undefined,
                url: path,
                referrer: '',
                title,
                ...(name ? { name } : {}),
                data: {
                    ...data,
                    area: areaName,
                    signed_in: distinctId !== null,
                    ...(distinctId ? { account_id: distinctId } : {}),
                    schema_version: 1,
                },
            })),
        ).catch(() => undefined);
        return true;
    } catch {
        return false;
    }
}

export function trackAccountEvent(event: AccountEvent, data: EventData = {}): void {
    send('account', event, data);
}

/**
 * Tracks a step in the Podcast Boost funnel. Guests are tracked anonymously; signed-in buyers
 * carry their opaque account ID, and accounts the server excludes (such as admins) are skipped.
 */
export function trackBoostEvent(event: BoostEvent, data: EventData = {}): void {
    send('boost', event, data);
}

/**
 * Boost pages are grouped so order IDs never reach analytics: every order page is reported as
 * `/podcast-boost/order`.
 */
function boostPathFor(path: string): string | null {
    if (!/^\/podcast-boost(?:\/|$)/.test(path)) return null;
    const normalized = path.replace(/\/+$/, '') || '/podcast-boost';
    return /^\/podcast-boost\/(?!mine$|status$)[^/]+$/.test(normalized)
        ? '/podcast-boost/order'
        : normalized;
}

export function trackPage(page: AnalyticsPage): void {
    const path = page.url.split(/[?#]/, 1)[0];
    const signedIn = Boolean(page.props.auth?.user);
    const distinctId = signedIn ? (page.props.analytics?.distinctId ?? null) : null;

    const nextAccountPath = signedIn && /^\/mina-sidor(?:\/|$)/.test(path) ? path : null;
    const nextBoostPath = signedIn && !distinctId ? null : boostPathFor(path);
    const previous = currentArea();

    accountPath = nextAccountPath;
    accountDistinctId = nextAccountPath ? distinctId : null;
    boostPath = nextBoostPath;
    boostDistinctId = nextBoostPath ? distinctId : null;

    const area = currentArea();
    if (area?.name !== previous?.name || area?.distinctId !== previous?.distinctId) {
        lastTrackedPath = null;
    }
    synchronizeIdentity();
    if (!area) {
        lastTrackedPath = null;
        return;
    }
    if (area.path !== lastTrackedPath && send(area.name)) lastTrackedPath = area.path;
}

export function initializeAnalytics(page: AnalyticsPage): void {
    trackPage(page);
    window.addEventListener(
        'load',
        () => {
            const area = currentArea();
            if (area && area.path !== lastTrackedPath && send(area.name)) {
                lastTrackedPath = area.path;
            }
        },
        { once: true },
    );
}
